import type { OrderLeg } from '../services/orders.service';
import { getDispatchScope } from './orderLegDiff';

// Pure-utility predicates extracted from OrderDetailsModalContent.tsx (the
// dispatch drawer). Each predicate answers "can the user edit this leg
// field right now?" given an explicit set of inputs — no React, no closures,
// no module state. The drawer keeps its own copies wired to its scope/edits
// for the moment; these utilities are intended for the /pro LegsGrid (Task
// 2.4) so that grid cells use the same locking rules as the drawer.
//
// Every predicate accepts a single `args` object so callers spell out exactly
// what they're passing in. `edits` is the editedLegs overlay map keyed by
// legrecno → partial overlay, the same shape used elsewhere in the codebase.

export type LegEdits = Record<number, Partial<OrderLeg>>;

// --- canEditDriver ---------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1500-1522.
// Locks when:
//   - driverAccess < 2, OR
//   - effective dispatcher is not GWI (CMS rejects non-GWI gwiMove sends)
//     UNLESS allowNonGwi is set (outside-carrier moves record the carrier's
//     SCAC as a pseudo-driver instead of dispatching a GWI unit, so the
//     non-GWI lock doesn't apply), OR
//   - any leg in the dispatch scope has isPaid === true.
// scopeLegs is expected to be the legs in the dispatch scope (drop-bounded
// run), already in order — the caller resolves it from getDispatchScope and
// the leg list before invoking this predicate.
//
// Historically the driver ALSO locked once the first scope leg had all four
// orig/dest arrival+depart timestamps filled (the "completed-move" lock). That
// was dropped so the dispatch-info "Remove Pay and Open Leg" (Clear Leg) action
// actually reopens the driver: clearing the pay line flips isPaid → false, and
// an UNPAID leg must stay re-assignable even when it's geofence-complete.
// isPaid (a driverpay_lineitem with a committed payrolldate) remains the guard
// that keeps a driver who's already been paid from being silently swapped.
// legstatus and the geofence timestamps are intentionally left intact, so a
// completed leg still renders as completed — only the driver reopens.
//
// allowNonGwi lifts ONLY the non-GWI-dispatcher reason. The paid-leg lock still
// holds — so a leg whose GWI driver has already been paid is never reopened and
// silently replaced by the outside carrier.
export function canEditDriver(args: {
  driverAccess: number;
  effectiveDispatcherIsGwi: boolean;
  scopeLegs: OrderLeg[];
  // Retained for call-site/API parity with the sibling guards; canEditDriver no
  // longer reads it (the timestamp-overlay completed-move check was removed).
  edits: LegEdits;
  allowNonGwi?: boolean;
}): boolean {
  if (args.driverAccess < 2) return false;
  if (!args.allowNonGwi && !args.effectiveDispatcherIsGwi) return false;
  if (args.scopeLegs.length === 0) return true;
  if (args.scopeLegs.some((l) => l.isPaid === true)) return false;
  return true;
}

// --- canEditDestination ----------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1528-1533.
// Locks when:
//   - legEditingAccess < 2, OR
//   - leg.isPaid === true, OR
//   - the NEXT leg is paid (this leg's destination is the same physical stop
//     as the next leg's origin — letting the cell stay editable would cascade
//     writes into the paid leg), OR
//   - the leg is a DELIVERY (legAction2 === 'DELIVERY') AND the user lacks the
//     delivery-location permission (canEditDeliveryLocation).
//
// The old blanket "DELIVERY destinations are always locked once persisted" rule
// is gone; delivery-location editing is now permission-gated instead. Managers
// (granted `leg-delivery-location`) edit delivery locations freely. Users
// without it can't touch a delivery location directly, but they can still run
// the rejection/re-delivery workflow: the move-type dropdown stays open
// (canEditLegAction2), so they convert the original delivery to REJECTED — at
// which point leg.legAction2 is no longer 'DELIVERY' and this cell unlocks. The
// "exactly one DELIVERY per order" invariant is enforced at save time by
// validateLegChanges (legValidation.ts). Because the grid feeds EFFECTIVE
// (edited) legs here, the lock/unlock tracks the user's in-flight type edit.

// --- Admin Override (#33501) -----------------------------------------------
// `overrideActive` means the pro is in Admin Override: an admin deliberately unlocked
// this record's state locks for one pro, for one session. It releases the PAID-LEG
// checks in the four guards below — the stop-shape cluster — because that is the driving
// case: a completed move whose pickup and stopoff labels were swapped. The drivers'
// routes were right; only the labels are wrong, and the customer's milestones depend on
// fixing them.
//
// Since 2026-09-14 (Lisa's call) it ALSO releases the paid-leg arms of the appointment
// and arrival/departure guards — the LC correcting a completed move usually has to fix
// the times alongside the labels, and the milestones the customer sees are built from
// them. Those timestamps do feed driver pay, so an override edit can desync what a
// driver was paid from what the record says: that is the trade being made deliberately,
// and it is exactly what the `override` history tag exists to make findable afterwards.
//
// It still does NOT reach canEditDriver. Reopening WHO was paid is a bigger decision
// than correcting when or where, and it stays carved out for the same reason as
// driver_pay_additions on the backend.
//
// Do not confuse `overrideActive` with `apptLockOverride`: the latter is the role-level
// `leg-appt-lock-override` grant, which lifts the SCHEDULING locks only and never the
// paid ones. Pro override supersedes it (resolveLegGates passes it through) and is the
// only thing that lifts a paid lock.
//
// The PERMISSION checks (legEditingAccess, canEditDeliveryLocation) are untouched in
// every case: override releases what the RECORD's state locked, never what the user's
// own grants withhold.

export function canEditDestination(args: {
  leg: OrderLeg;
  nextLeg: OrderLeg | null;
  legEditingAccess: number;
  // Whether the user holds the `leg-delivery-location` permission. Gates the
  // location cell ONLY on delivery legs; non-delivery legs ignore it.
  canEditDeliveryLocation: boolean;
  // Retained for call-site/API parity with the sibling guards.
  edits: LegEdits;
  overrideActive?: boolean;
}): boolean {
  if (args.legEditingAccess < 2) return false;
  if (!args.overrideActive) {
    if (args.leg.isPaid) return false;
    if (args.nextLeg?.isPaid) return false;
  }
  if (args.leg.legAction2 === 'DELIVERY' && !args.canEditDeliveryLocation) return false;
  return true;
}

// --- canEditOrigin ---------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1539-1551.
// Locks when:
//   - legEditingAccess < 2, OR
//   - leg.isPaid, OR
//   - the leg is not the first leg AND the previous leg's destination is not
//     editable (origin is the same physical stop as the previous leg's dest).
// Because it delegates to canEditDestination(prevLeg), the delivery-location
// permission gate rides along automatically: the origin of the leg that FOLLOWS
// a delivery is the same physical stop as the delivery location, so it locks for
// users without the permission and unlocks once the prev leg stops being a
// DELIVERY (e.g. converted to REJECTED).
export function canEditOrigin(args: {
  leg: OrderLeg;
  prevLeg: OrderLeg | null;
  legEditingAccess: number;
  canEditDeliveryLocation: boolean;
  edits: LegEdits;
  overrideActive?: boolean;
}): boolean {
  if (args.legEditingAccess < 2) return false;
  if (!args.overrideActive && args.leg.isPaid) return false;
  if (!args.prevLeg) return true;
  // For the prev leg's dest check, this leg IS the prev leg's nextLeg.
  return canEditDestination({
    leg: args.prevLeg,
    nextLeg: args.leg,
    legEditingAccess: args.legEditingAccess,
    canEditDeliveryLocation: args.canEditDeliveryLocation,
    edits: args.edits,
    overrideActive: args.overrideActive,
  });
}

// --- canEditLegtype --------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1556-1564.
// legtype lives at the boundary between the previous leg's destination and
// this leg's origin. Locks when:
//   - legEditingAccess < 2, OR
//   - leg.isPaid, OR
//   - the previous leg is paid (the boundary's other side is locked).
//
// A DELIVERY legtype is NO LONGER locked (legacy parity). See canEditDestination
// for the full rationale — the rejection/re-delivery workflow needs the delivery
// boundary editable, and the one-DELIVERY-per-order invariant is enforced at
// save time by validateLegChanges.
export function canEditLegtype(args: {
  leg: OrderLeg;
  prevLeg: OrderLeg | null;
  legEditingAccess: number;
  // Retained for call-site/API parity with the sibling guards.
  edits: LegEdits;
  overrideActive?: boolean;
}): boolean {
  if (args.legEditingAccess < 2) return false;
  if (!args.overrideActive) {
    if (args.leg.isPaid) return false;
    if (args.prevLeg && args.prevLeg.isPaid) return false;
  }
  return true;
}

// --- canEditLegAction2 -----------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1569-1577.
// legAction2 lives at the boundary between this leg's destination and the
// next leg's origin. Locks when:
//   - legEditingAccess < 2, OR
//   - leg.isPaid, OR
//   - the next leg is paid.
//
// A DELIVERY legAction2 is NO LONGER locked (legacy parity). This is the field
// the dispatcher flips to REJECTED on the original delivery leg after adding a
// re-delivery stop. See canEditDestination for the full rationale; the
// one-DELIVERY-per-order invariant is enforced at save time by
// validateLegChanges.
export function canEditLegAction2(args: {
  leg: OrderLeg;
  nextLeg: OrderLeg | null;
  legEditingAccess: number;
  // Retained for call-site/API parity with the sibling guards.
  edits: LegEdits;
  overrideActive?: boolean;
}): boolean {
  if (args.legEditingAccess < 2) return false;
  if (!args.overrideActive) {
    if (args.leg.isPaid) return false;
    if (args.nextLeg && args.nextLeg.isPaid) return false;
  }
  return true;
}

// --- canEditLegDrop --------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx canEditLegDrop.
// Live/Drop toggle. Locks only when legEditingAccess < 2 — paid legs remain
// toggleable.
export function canEditLegDrop(args: { legEditingAccess: number }): boolean {
  if (args.legEditingAccess < 2) return false;
  return true;
}

// --- canEditArrivalDepart --------------------------------------------------
// Origin/destination arrival and departure timestamps. Locks when:
//   - arrivalDepartAccess < 2 (user lacks permission), OR
//   - leg.isPaid (paid legs are immutable — driver pay was calculated against
//     these exact timestamps; changing them would desync payroll).
export function canEditArrivalDepart(args: {
  leg: OrderLeg;
  arrivalDepartAccess: number;
  overrideActive?: boolean;
}): boolean {
  // The PERMISSION is never released — override lifts record state, not grants.
  if (args.arrivalDepartAccess < 2) return false;
  if (args.leg.isPaid && !args.overrideActive) return false;
  return true;
}

// --- isApptLockedScope -----------------------------------------------------
// Appointment dates are owned by the scheduling process, not by inline leg
// editing, for any leg that belongs to a pickup or delivery move. A leg's appt
// dates lock when ANY leg in its dispatch scope carries one of the move types
// below.
//
// "Dispatch scope" is the existing drop-bounded run (getDispatchScope): the
// legs from just after the previous legDrop='Y' through the next legDrop='Y'
// inclusive. Two consequences fall out of that and are intentional:
//   - A LIVE delivery keeps the next leg in the same scope, so the delivery
//     leg's destApptDate AND the next leg's origApptDate both lock. Those two
//     values are already kept identical by the updateLegField cascade.
//   - A DROP delivery is the LAST leg of its scope, so the following leg lands
//     in the next scope and stays editable unless that scope independently
//     qualifies. Correct: on a drop the container sits at the delivery site and
//     the next leg picks it up on its own date (the cascade is suppressed across
//     drops for the same reason).
//
// Callers pass EFFECTIVE (edit-overlaid) legs, so a staged move-type change
// re-locks or unlocks immediately without this predicate knowing about the
// overlay. Both LegsGrid resolveLegGates call sites already do this.
//
// Adding a value to either set is the whole change for the deferred expansion
// to legtype='DELIVERY' (delivery+1 leg) and legaction2='TERMINATE'.
const APPT_LOCK_LEGTYPES = new Set(['PICKUP']);
const APPT_LOCK_LEGACTION2 = new Set(['DELIVERY']);

export function isApptLockedScope(args: {
  leg: OrderLeg;
  allLegs: OrderLeg[];
}): boolean {
  const scope = new Set(getDispatchScope(args.leg.legrecno, args.allLegs));
  return args.allLegs.some(
    (l) =>
      scope.has(l.legrecno) &&
      (APPT_LOCK_LEGTYPES.has((l.legtype || '').trim().toUpperCase()) ||
        APPT_LOCK_LEGACTION2.has((l.legAction2 || '').trim().toUpperCase())),
  );
}

// --- apptLockApplies ------------------------------------------------------
// The scope lock (above) is structural — it says "this leg belongs to a
// pickup/delivery move". Whether it actually LOCKS a given appt-date cell adds
// two conditions on top:
//
//   * `savedApptDate` must be non-blank. The lock exists to protect an
//     appointment that was already agreed with the customer/terminal; there is
//     nothing to protect when none was ever saved. This is also what lets a user
//     fill in a brand-new stopoff leg's date, and it matches the server guard,
//     which allows blank -> date (that allowance is load-bearing: Smart Dispatch
//     autofills a missing appt date to today when assigning a driver).
//
//     It MUST be the SAVED value, never the effective/overlaid one. The grid is
//     fed effective legs, and gating on those would re-lock the cell the instant
//     the user picked a date — they could set it once and never correct it.
//     Callers resolve this from editing.originalLegs; a leg with no saved twin is
//     new, so blank.
//
//   * the user must not hold `leg-appt-lock-override` (level 2), which lets
//     dispatch edit agreed appointments in place.
//
//   * ...or `leg-appt-edit` (level 2) while the move is UNDISPATCHED. This is the
//     warehouse-LC grant: they set an appointment and must be able to move it to the
//     next day themselves, which today they cannot — the scope lock catches their own
//     date the moment it is saved. It differs from the dispatch override in the one
//     way that matters to them: it stops at a driver. Once anything in the dispatch
//     scope is assigned, the date is a commitment to a driver rather than a plan, and
//     the cell re-locks.
//
//     The driver test is canClearAppt's, deliberately: that is the rule already in
//     force for the X beside the same cell, so the two halves of "change this date"
//     cannot disagree. '000' counts as assigned there and so it does here. Requiring
//     EVERY leg in the scope to be blank fails closed on divergent drivers, which is
//     a data error.
function scopeHasDriver(leg: OrderLeg, allLegs: OrderLeg[]): boolean {
  const scope = new Set(getDispatchScope(leg.legrecno, allLegs));
  return allLegs
    .filter((l) => scope.has(l.legrecno))
    .some((l) => String(l.driver ?? '').trim() !== '');
}

function apptLockApplies(args: {
  leg: OrderLeg;
  allLegs: OrderLeg[];
  savedApptDate?: string | null;
  apptLockOverride?: boolean;
  /** `leg-appt-edit` >= 2 — lifts the scope lock only while nothing in the
   *  dispatch scope has a driver. */
  apptEdit?: boolean;
}): boolean {
  if (args.apptLockOverride) return false;
  if (!String(args.savedApptDate ?? '').trim()) return false;
  if (!isApptLockedScope({ leg: args.leg, allLegs: args.allLegs })) return false;
  if (args.apptEdit && !scopeHasDriver(args.leg, args.allLegs)) return false;
  return true;
}

// --- canClearAppt ----------------------------------------------------------
// Gates the clear (X) button that sits beside every appt-date cell. On a cell
// locked by isApptLockedScope the X is the ONLY way to change the value, so the
// driver check applies there; on an unlocked cell the X is just a shortcut for
// the DateTimePicker's own clear (which already permits clearing with a driver
// assigned), so gating it harder than the adjacent picker would be incoherent.
//
// All legs in a scope are expected to carry the same driver. Divergent drivers
// within a scope is a data error, so requiring EVERY leg to be blank fails
// closed on it.
export function canClearAppt(args: {
  leg: OrderLeg;
  allLegs: OrderLeg[];
  /** Resolved `leg-appt-clear` component access level. */
  apptClearAccess: number;
  /** Effective (overlay-applied) appt date for the cell being cleared. */
  currentValue: string | null | undefined;
  /** `leg-appt-lock-override` >= 2. A superset of leg-appt-clear: it grants the
   *  clear outright AND waives the driver-blank requirement, so granting only
   *  the override doesn't leave a dispatcher able to edit but not clear. */
  apptLockOverride?: boolean;
  /** Admin Override — the only thing that lifts a paid lock. */
  overrideActive?: boolean;
}): boolean {
  if (args.apptClearAccess < 2 && !args.apptLockOverride) return false;
  if (!String(args.currentValue ?? '').trim()) return false;
  if (args.leg.isPaid && !args.overrideActive) return false;
  if (args.apptLockOverride) return true;
  if (!isApptLockedScope({ leg: args.leg, allLegs: args.allLegs })) return true;
  // The same helper apptLockApplies uses for leg-appt-edit. Shared rather than
  // duplicated: the comment there says the two halves of "change this date" cannot
  // disagree, and two copies of the scan could drift into disagreeing.
  return !scopeHasDriver(args.leg, args.allLegs);
}

// --- planDownstreamApptClears ----------------------------------------------
// Clearing the DELIVERY appointment used to leave every LATER leg's appointment
// standing, which drops the pro straight into the LC "Missing Appointment"
// bucket. That bucket fires on an out-of-order gap — an earlier leg missing one
// of its dates while a later leg still has one (lc_dashboards.py,
// 'lc-sched-missing-appointment'). The terminate leg is the usual offender: an
// LC removes the delivery date and the pro immediately reappears on their board.
//
// So a delivery-appointment clear also clears the appt dates on the legs AFTER
// the delivery leg. This plans that fan-out; it does not apply it.
//
// It is deliberately conservative — a later leg already in motion keeps its
// appointment, because that date is no longer just a plan:
//   * 'driver'  — a driver is assigned. '000' counts as assigned, matching
//                 canClearAppt above.
//   * 'arrived' — any arrival/departure timestamp is stamped on the leg.
//   * 'scope'   — the leg's dispatch scope is appt-locked and a SIBLING leg in
//                 that scope carries a driver. That is exactly the condition
//                 canClearAppt above refuses to clear on, so the fan-out must
//                 refuse it too — otherwise the X does indirectly what it
//                 declines to do when pointed straight at that cell. It is also
//                 the rule the server states for this shape
//                 (backend/app/utils/leg_appt_guard.py), though note that guard
//                 runs on update_leg_details; the grid saves through
//                 save_order_details, which does not call it.
//
// Skips are returned, not swallowed: a partial clear leaves the pro in the
// bucket, so the LC has to be told which legs kept their dates.
//
// This rule has a TWIN: plan_downstream_appt_clears in
// backend/app/utils/leg_appt_guard.py, which the Reschedule Requested trouble
// ticket runs (reschedule_trigger_service / reschedule_native). That path used
// to clear only the delivery leg's dispatch group, so it stopped at the first
// legdrop='Y' and stranded a terminate leg past it — bugfix/#33627. The two
// must agree case for case; the shared table in
// backend/tests/test_leg_appt_downstream_plan.py mirrors the
// planDownstreamApptClears block of legFieldGuards.test.ts. The grid cannot
// simply call the server rule: it plans against edit-overlaid legs
// (editing.effectiveLegs) that only exist in the browser.
//
// Legs with nothing left to clear appear in neither list. On a LIVE delivery
// that includes the origApptDate of the leg right after it — the same physical
// appointment as the delivery leg's destApptDate, already blanked by the
// updateLegField cascade, so this plan does not claim credit for it.

export type DownstreamApptSkipReason = 'driver' | 'arrived' | 'scope';

/** Why a downstream leg kept its appointment date, in the user's words. Lives
 *  here so every surface that reports a skip says the same thing — the grid's
 *  reschedule modal and the Reschedule Requested ticket, whose skips come from
 *  the Python twin of this rule (backend/app/utils/leg_appt_guard.py). */
export const DOWNSTREAM_SKIP_REASON_LABELS: Record<DownstreamApptSkipReason, string> = {
  driver: 'driver assigned',
  arrived: 'already arrived',
  scope: 'driver assigned in its dispatch group',
};

export interface DownstreamApptClear {
  legrecno: number;
  legnum: number;
}

export interface DownstreamApptSkip {
  legrecno: number;
  legnum: number;
  reason: DownstreamApptSkipReason;
}

export interface DownstreamApptClearPlan {
  clears: DownstreamApptClear[];
  skips: DownstreamApptSkip[];
}

const hasDate = (v: unknown): boolean => !!String(v ?? '').trim();

/** Any arrival/departure stamp on the leg — the move has physically started. */
function hasMovementStamp(leg: OrderLeg): boolean {
  return [
    leg.origArrivalDateTime,
    leg.origDepartDateTime,
    leg.destArrivalDateTime,
    leg.destDepartDateTime,
  ].some(hasDate);
}

export function planDownstreamApptClears(args: {
  /** Effective (edit-overlaid) legs, ordered by legnum. */
  allLegs: OrderLeg[];
  /** The order's delivery legnum. */
  deliveryLeg: number;
}): DownstreamApptClearPlan {
  const clears: DownstreamApptClear[] = [];
  const skips: DownstreamApptSkip[] = [];

  const ordered = [...args.allLegs].sort((a, b) => a.legnum - b.legnum);
  const deliveryIdx = ordered.findIndex((l) => l.legnum === args.deliveryLeg);
  if (deliveryIdx === -1) return { clears, skips };
  // On a live delivery the cascade already owns the next leg's origApptDate.
  const cascadeOwnsOrigOf =
    ordered[deliveryIdx].legDrop !== 'Y' ? ordered[deliveryIdx + 1]?.legrecno : undefined;

  for (const leg of ordered.slice(deliveryIdx + 1)) {
    const pending =
      hasDate(leg.destApptDate) ||
      (leg.legrecno !== cascadeOwnsOrigOf && hasDate(leg.origApptDate));
    if (!pending) continue;

    const entry = { legrecno: leg.legrecno, legnum: leg.legnum };
    if (hasDate(leg.driver)) {
      skips.push({ ...entry, reason: 'driver' });
      continue;
    }
    if (hasMovementStamp(leg)) {
      skips.push({ ...entry, reason: 'arrived' });
      continue;
    }
    if (isApptLockedScope({ leg, allLegs: args.allLegs })) {
      const scope = new Set(getDispatchScope(leg.legrecno, args.allLegs));
      const scopeDriverFree = args.allLegs
        .filter((l) => scope.has(l.legrecno))
        .every((l) => !hasDate(l.driver));
      if (!scopeDriverFree) {
        skips.push({ ...entry, reason: 'scope' });
        continue;
      }
    }
    clears.push(entry);
  }

  return { clears, skips };
}

// --- canEditDestAppt -------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1600-1604.
// Locks when:
//   - leg.isPaid, OR
//   - the NEXT leg is paid AND this leg is NOT a drop (when legDrop='Y' the
//     destApptDate cascade in updateLegField doesn't fire across the boundary,
//     so editing the dest appt cannot mutate the paid leg's data), OR
//   - the leg's dispatch scope is appt-locked (see isApptLockedScope).
//
// History of the DELIVERY rule on this cell, in order:
//   1. V1 / early gwi360 — legAction2==='DELIVERY' blocked the cell outright.
//   2. Then unlocked, with edits routed through the required
//      RescheduleEditModal in LegDateCell so the audit trail survived.
//   3. Now locked again by the scope rule, and MORE broadly: a DELIVERY leg is
//      by definition in a DELIVERY scope, and so is every other leg in that
//      scope. The reschedule modal moved onto the clear (X) button, which is
//      the only remaining way to change the value from this grid.
//
// Note: the drawer's canEditDestAppt does NOT gate on legEditingAccess —
// preserved here to match drawer behavior exactly.
export function canEditDestAppt(args: {
  leg: OrderLeg;
  nextLeg: OrderLeg | null;
  allLegs: OrderLeg[];
  /** SAVED destApptDate — see apptLockApplies. */
  savedApptDate?: string | null;
  /** `leg-appt-lock-override` >= 2. Lifts the SCHEDULING locks only. */
  apptLockOverride?: boolean;
  /** `leg-appt-edit` >= 2. Lifts the scope lock only while the dispatch scope
   *  carries no driver — see apptLockApplies. */
  apptEdit?: boolean;
  /** Admin Override — the only thing that lifts a paid lock. */
  overrideActive?: boolean;
}): boolean {
  // `apptLockOverride` (the role-level grant) does NOT lift these; Admin Override does.
  if (!args.overrideActive) {
    if (args.leg.isPaid) return false;
    if (args.nextLeg?.isPaid && args.leg.legDrop !== 'Y') return false;
  }
  if (
    apptLockApplies({
      leg: args.leg,
      allLegs: args.allLegs,
      savedApptDate: args.savedApptDate,
      apptLockOverride: args.apptLockOverride,
      apptEdit: args.apptEdit,
    })
  ) {
    return false;
  }
  return true;
}

// --- canEditOrigAppt -------------------------------------------------------
// Drawer source: OrderDetailsModalContent.tsx ~lines 1609-1627.
// Locks when:
//   - leg.isPaid, OR
//   - the leg's dispatch scope is appt-locked (see isApptLockedScope), OR
//   - leg is not the first leg AND the previous leg is a live delivery
//     (legAction2 === 'DELIVERY' AND legDrop !== 'Y') AND the previous leg
//     has a non-empty destApptDate.
//
// `leg-appt-lock-override` lifts BOTH appointment locks — the scope lock and the
// legacy prev-live-delivery clause — so a holder can edit an agreed appointment
// in place. It does NOT lift the paid locks.
//
// Parity quirk: the drawer reads `prevLeg.destApptDate`, `prevLeg.legAction2`,
// and `prevLeg.legDrop` DIRECTLY — not via getLegValue / overlay. We mirror
// that here so behavior matches the drawer 1:1. Worth verifying when wiring
// this into /pro LegsGrid: if grid-side edits to those fields should change
// the gate immediately, swap to effectiveLegValue and update both sites.
export function canEditOrigAppt(args: {
  leg: OrderLeg;
  prevLeg: OrderLeg | null;
  allLegs: OrderLeg[];
  /** SAVED origApptDate — see apptLockApplies. */
  savedApptDate?: string | null;
  /** `leg-appt-lock-override` >= 2. Lifts the SCHEDULING locks only. */
  apptLockOverride?: boolean;
  /** `leg-appt-edit` >= 2. Lifts the scope lock only while the dispatch scope
   *  carries no driver — see apptLockApplies. */
  apptEdit?: boolean;
  /** Admin Override — the only thing that lifts a paid lock. */
  overrideActive?: boolean;
}): boolean {
  if (args.leg.isPaid && !args.overrideActive) return false;
  if (
    apptLockApplies({
      leg: args.leg,
      allLegs: args.allLegs,
      savedApptDate: args.savedApptDate,
      apptLockOverride: args.apptLockOverride,
      apptEdit: args.apptEdit,
    })
  ) {
    return false;
  }
  if (args.apptLockOverride) return true;
  if (!args.prevLeg) return true;
  const prev = args.prevLeg;
  // Boundary integrity: only block when prev is paid AND prev is NOT a drop.
  // On a drop boundary, the origApptDate cascade in updateLegField doesn't
  // fire (it checks `legDropOf(prevLeg) !== 'Y'`), so editing this leg's
  // orig appt cannot mutate the paid prev leg's destApptDate.
  // Under Admin Override this arm is released too, which means the origApptDate cascade
  // CAN now rewrite the paid previous leg's destApptDate. That is the point — the two
  // sides of a boundary are one appointment, and correcting only half would leave the
  // move inconsistent — but it is a real write onto a paid leg, hence the audit tag.
  if (prev.isPaid && prev.legDrop !== 'Y' && !args.overrideActive) return false;
  if (
    prev.legAction2 === 'DELIVERY' &&
    prev.legDrop !== 'Y' &&
    prev.destApptDate &&
    prev.destApptDate.trim() !== ''
  ) {
    return false;
  }
  return true;
}
